import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import SendIcon from "@mui/icons-material/Send";
import SpeakerNotesOffOutlinedIcon from "@mui/icons-material/SpeakerNotesOffOutlined";
import { useTranslation } from "react-i18next";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";
import ChatMessage from "../../League/components/ChatMessage";
import AttendeePage from "../../../components/RoundComponents/AttendeePage";
import {
  MAX_LENGTH,
  mergeRows,
  newClientId,
} from "../../League/components/ChatTab";
import { subscribeToChat } from "../chatSocket";
import ChatGate, { gateNeeded } from "./ChatGate";
import ReportDialog from "./ReportDialog";

const PAGE = 30;
const NEAR_BOTTOM = 80;

const ThreadView = ({ threadId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listRef = React.useRef(null);
  const bottomRef = React.useRef(null);
  const prevHeightRef = React.useRef(null);
  const viewerIdRef = React.useRef(null);
  const pendingSendRef = React.useRef(null);

  const [loading, setLoading] = React.useState(true);
  const [gone, setGone] = React.useState(null);
  const [thread, setThread] = React.useState(null);
  const [config, setConfig] = React.useState(null);
  const [muted, setMuted] = React.useState(false);
  const [messages, setMessages] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [cursor, setCursor] = React.useState(null);
  const [loadingOlder, setLoadingOlder] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [gateOpen, setGateOpen] = React.useState(false);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [matchScore, setMatchScore] = React.useState(0);
  const [card, setCard] = React.useState({ mounted: false, open: false });

  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM;
  };

  const scrollToBottom = (behavior = "auto") => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior });
  };

  const markRead = React.useCallback(() => {
    request("eventchat/read", "post", { threadId });
  }, [threadId]);

  const load = React.useCallback(
    async ({ replace = false } = {}) => {
      const result = await request(
        `eventchat/messages?threadId=${threadId}&limit=${PAGE}`
      );
      if (!result?.success) {
        if (result?.networkError && !replace) return result;
        setGone(
          result?.code === "CHAT_CLOSED"
            ? t("chat.closed.body")
            : t("chat.thread.unavailable")
        );
        setLoading(false);
        return result;
      }

      viewerIdRef.current = result.viewerId;
      setThread((current) => ({
        ...result.thread,
        other: result.thread.other?.firstName ? result.thread.other : current?.other,
      }));
      setMuted(!!result.muted);
      setMatchScore(result.matchScore ?? 0);
      setConfig({
        terms: result.terms,
        termsAccepted: result.termsAccepted,
      });

      const rows = result.messages || [];
      if (replace) {
        setMessages(rows);
        setHasMore(!!result.hasMore);
        setCursor(result.nextCursor || null);
      } else {
        setMessages((prev) => mergeRows(prev, rows));
      }
      setLoading(false);
      if (result.thread.unread > 0) markRead();
      return result;
    },
    [threadId, markRead, t]
  );

  React.useEffect(() => {
    setLoading(true);
    setGone(null);
    load({ replace: true });
  }, [load]);

  React.useEffect(() => {
    if (!loading && messages.length) scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  React.useEffect(() => {
    const onMessage = (payload) => {
      if (String(payload.threadId) !== String(threadId)) return;
      const message = payload.message;
      const mine =
        !!viewerIdRef.current &&
        String(message.authorId) === String(viewerIdRef.current);
      const atBottom = isNearBottom();
      setMessages((prev) => mergeRows(prev, [{ ...message, isMine: mine }]));
      if (!mine) markRead();
      if (atBottom) {
        window.requestAnimationFrame(() => scrollToBottom("smooth"));
      }
    };
    const onUpdated = (payload) => {
      if (String(payload.threadId) !== String(threadId)) return;
      load();
    };
    return subscribeToChat({ onMessage, onUpdated, onConnect: () => load() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, load]);

  React.useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || prevHeightRef.current === null) return;
    el.scrollTop = el.scrollHeight - prevHeightRef.current;
    prevHeightRef.current = null;
  }, [messages]);

  const loadOlder = async () => {
    if (!cursor || loadingOlder) return;
    setLoadingOlder(true);
    const result = await request(
      `eventchat/messages?threadId=${threadId}&limit=${PAGE}&before=${cursor}`
    );
    if (result?.success) {
      prevHeightRef.current = listRef.current?.scrollHeight || 0;
      setMessages((prev) => mergeRows(prev, result.messages || []));
      setHasMore(!!result.hasMore);
      setCursor(result.nextCursor || null);
    }
    setLoadingOlder(false);
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop < 60 && hasMore && !loadingOlder) loadOlder();
  };

  const markFailed = (clientId) =>
    setMessages((prev) =>
      prev.map((m) =>
        m.clientId === clientId ? { ...m, pending: false, failed: true } : m
      )
    );

  const deliver = async (text, clientId) => {
    setSending(true);
    const result = await request("eventchat/send", "post", {
      threadId,
      text,
      clientId,
    });
    setSending(false);

    if (result?.success) {
      setMessages((prev) => mergeRows(prev, [result.message]));
      return;
    }

    markFailed(clientId);
    if (result?.code === "TERMS_REQUIRED") {
      const fresh = await load();
      if (fresh?.success && gateNeeded(fresh)) {
        pendingSendRef.current = { text, clientId };
        setGateOpen(true);
        return;
      }
      if (fresh?.success) toast.error(result?.error || t("chat.error.send"));
      return;
    }
    toast.error(result?.error || t("chat.error.send"));
    if (result?.code === "CHAT_CLOSED") setGone(t("chat.closed.body"));
  };

  const queue = (text) => {
    const clientId = newClientId();
    setMessages((prev) => [
      ...prev,
      {
        _id: clientId,
        clientId,
        text,
        authorType: "player",
        isMine: true,
        deleted: false,
        pending: true,
        createdAt: new Date().toISOString(),
      },
    ]);
    window.requestAnimationFrame(() => scrollToBottom("smooth"));
    return clientId;
  };

  const send = () => {
    const text = draft.trim();
    if (!text || sending) return;
    if (gateNeeded(config)) {
      pendingSendRef.current = { text, clientId: null };
      setGateOpen(true);
      return;
    }
    setDraft("");
    deliver(text, queue(text));
  };

  const handleGateDone = (changes) => {
    setGateOpen(false);
    setConfig((current) => ({ ...current, ...changes }));
    const pending = pendingSendRef.current;
    pendingSendRef.current = null;
    if (!pending) return;
    if (pending.clientId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.clientId === pending.clientId
            ? { ...m, failed: false, pending: true }
            : m
        )
      );
      deliver(pending.text, pending.clientId);
      return;
    }
    setDraft("");
    deliver(pending.text, queue(pending.text));
  };

  const retry = (message) => {
    if (sending) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.clientId === message.clientId
          ? { ...m, failed: false, pending: true }
          : m
      )
    );
    deliver(message.text, message.clientId);
  };

  const discard = (message) =>
    setMessages((prev) => prev.filter((m) => m.clientId !== message.clientId));

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (gone) {
    return (
      <Box
        sx={{
          px: 3,
          pt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "action.hover",
            mb: 2.5,
          }}
        >
          <SpeakerNotesOffOutlinedIcon
            sx={{ fontSize: 32, color: "text.secondary" }}
          />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 3, maxWidth: 280 }}>
          {gone}
        </Typography>
        <Button
          className="tnfm-pill-btn"
          variant="contained"
          style={{ paddingLeft: 28, paddingRight: 28 }}
          onClick={() => navigate("/chats")}
        >
          {t("chat.back")}
        </Button>
      </Box>
    );
  }

  const other = thread?.other || {};
  const firstName = other.firstName || t("chat.thread.someone");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100dvh - 76px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <IconButton aria-label={t("chat.back")} onClick={() => navigate("/chats")}>
          <ArrowBackIcon />
        </IconButton>
        <ButtonBase
          aria-label={t("chat.thread.viewprofile", { name: firstName })}
          onClick={() => setCard({ mounted: true, open: true })}
          sx={{
            flex: 1,
            minWidth: 0,
            justifyContent: "flex-start",
            gap: 1,
            py: 0.5,
            borderRadius: 2,
            textAlign: "left",
          }}
        >
          <Avatar src={other.profileImage || undefined} sx={{ width: 36, height: 36 }}>
            {firstName.charAt(0)}
          </Avatar>
          <Typography sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
            {[other.firstName, other.lastName ? `${other.lastName}.` : null]
              .filter(Boolean)
              .join(" ") || firstName}
          </Typography>
        </ButtonBase>
        <IconButton
          aria-label={t("chat.report.title", { name: firstName })}
          onClick={() => setReportOpen(true)}
        >
          <FlagOutlinedIcon />
        </IconButton>
      </Box>

      <Box
        ref={listRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          px: 2,
          pt: 1,
        }}
      >
        {loadingOlder && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {messages.length === 0 && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "#68C371",
                mb: 2,
              }}
            >
              <FavoriteIcon sx={{ fontSize: 28, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>
              {t("chat.thread.empty.title", { name: firstName })}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              {t("chat.thread.empty.body")}
            </Typography>
          </Box>
        )}
        {messages.map((message) => (
          <ChatMessage
            key={message.clientId || message._id}
            message={{
              ...message,
              authorName: message.isMine ? message.authorName : firstName,
            }}
            onRetry={retry}
            onDiscard={discard}
          />
        ))}
        <div ref={bottomRef} />
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        {muted ? (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {t("chat.thread.muted")}
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
              <TextField
                multiline
                maxRows={4}
                size="small"
                fullWidth
                placeholder={t("chat.thread.placeholder", { name: firstName })}
                value={draft}
                inputProps={{ maxLength: MAX_LENGTH, enterKeyHint: "send" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    paddingLeft: "18px",
                    paddingRight: "18px",
                  },
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                    {
                      borderWidth: "1px",
                    },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderWidth: "1px",
                    },
                }}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" || e.shiftKey) return;
                  if (e.nativeEvent?.isComposing) return;
                  e.preventDefault();
                  send();
                }}
              />
              <IconButton
                color="primary"
                aria-label={t("chat.send")}
                disabled={!draft.trim() || sending}
                onClick={send}
              >
                {sending ? <CircularProgress size={20} /> : <SendIcon />}
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      <ChatGate
        open={gateOpen}
        eventId={thread?.eventId}
        config={config}
        onDone={handleGateDone}
        onCancel={() => {
          pendingSendRef.current = null;
          setGateOpen(false);
        }}
      />
      {card.mounted && (
        <AttendeePage
          attendee={{
            _id: other._id,
            firstName: other.firstName,
            lastName: other.lastName,
            profileImage: other.profileImage,
            matchScore,
          }}
          open={card.open}
          close={() => setCard({ mounted: true, open: false })}
          onExited={() => setCard({ mounted: false, open: false })}
          pairingEventType="mixer"
          eventId={thread?.eventId}
          chatEnabled
          hideChat
        />
      )}
      <ReportDialog
        open={reportOpen}
        threadId={threadId}
        name={firstName}
        onClose={() => setReportOpen(false)}
        onReported={() => navigate("/chats")}
      />
    </Box>
  );
};

export default ThreadView;
